import * as React from "react";
import { observer } from "mobx-react";

import { QueryRoute } from "bernie-core";
import { Layer } from "bernie-view";

import { useLocalization } from "@shared-ui/localization-context";

import { UitkLink } from "uitk-react-link";
import { UitkHeading, UitkHeadingProps } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";

import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";

import { InfoDialog } from "../InfoDialog/InfoDialog";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import { CampaignVR } from "../CampaignVR/CampaignVR";
import { CampaignDescription } from "../CampaignDescription/CampaignDescription";

import Campaign = CampaignRecommendationsWithOffersQuery.Campaigns;

export interface CampaignHeaderProps {
  campaign: Campaign;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  campaignTitle?: string;
  campaignCopyAlignment?: string;
  showCampaignDescription?: boolean;
  showCampaignTitle?: boolean;
  showCampaignTermsAndConditions?: boolean;
  view?: string;
  imgSrc?: string;
  context?: ExtendedContextStore;
}

// header of the campaigns with title, subtitle and terms and conditions
export const CampaignHeader = observer(
  ({
    campaign,
    openDialog,
    closeDialog,
    campaignTitle,
    campaignCopyAlignment,
    showCampaignDescription,
    showCampaignTitle,
    showCampaignTermsAndConditions,
    view,
    imgSrc,
    context,
  }: CampaignHeaderProps) => {
    const { formatText } = useLocalization();
    const isHavingOriginCity = campaign.fromOriginHeaderText;
    const defaultHeadingConfig: UitkHeadingProps = { level: 2, size: 700 };
    const centeredHeadingConfig: UitkHeadingProps = { level: 2, size: 800 };
    const campaignHeading = campaignTitle || campaign.headline;
    const isCentered = campaignCopyAlignment === "centered";
    const headingConfig = isCentered ? centeredHeadingConfig : defaultHeadingConfig;
    const showDescription = showCampaignDescription !== undefined ? showCampaignDescription : true;
    const showTitle = showCampaignTitle !== undefined ? showCampaignTitle : true;
    const showTermsAndConditions = showCampaignTermsAndConditions !== undefined ? showCampaignTermsAndConditions : true;
    const image = imgSrc || "";

    if (!showDescription && !showTitle && !showTermsAndConditions) {
      return null;
    }

    if (view === "carousel-vr" && image !== "") {
      return <CampaignVR campaign={campaign} campaignTitle={campaignTitle} context={context} imgSrc={imgSrc} />;
    }

    return (
      <UitkSpacing padding={{ blockstart: isCentered ? "three" : undefined }}>
        <div className={isCentered ? "center-campaignCopyAlignment" : "CampaignHeader"} data-testid="campaign-header">
          {showTitle && <UitkHeading {...headingConfig}>{campaignHeading}</UitkHeading>}
          <UitkSpacing padding={{ blockstart: isCentered ? "one" : undefined }}>
            <div>
              <CampaignDescription
                campaign={campaign}
                isCentered={isCentered}
                isHavingOriginCity={isHavingOriginCity}
                showDescription={showDescription}
              />
              {campaign.termsAndConditions && showTermsAndConditions && (
                <>
                  <UitkLink
                    className={isCentered ? "center_termsAndConditions" : "terms-conditions-text"}
                    inline={!isCentered}
                    onClick={openDialog.bind(this, `terms-conditions-${campaign.id}`)}
                  >
                    <button type="button">{formatText("merchCampaign.termsConditions.label")}</button>
                  </UitkLink>
                  <QueryRoute query={{ pwaDialog: `terms-conditions-${campaign.id}` }}>
                    <Layer id={`terms-conditions-dialog-${campaign.id}`}>
                      <InfoDialog
                        infoText={campaign.termsAndConditions}
                        closeDialog={closeDialog}
                        buttonText={formatText("DestinationsByAffinity.priceDialogCloseButton")}
                      />
                    </Layer>
                  </QueryRoute>
                </>
              )}
            </div>
          </UitkSpacing>
        </div>
      </UitkSpacing>
    );
  }
);
